/**
 * 1.18.0 — Классификатор ошибок outbox.
 *
 * Различает категории ошибок чтобы решить retry vs dead_letter.
 *
 *  retryable — есть смысл повторить:
 *    • сетевые: TypeError, AbortError, ERR_NETWORK, fetch failed
 *    • timeout
 *    • 5xx (сервер временно недоступен)
 *    • 429 Rate limit
 *
 *  permanent — повтор бесполезен:
 *    • 400 Bad Request — данные не пройдут даже после retry
 *    • 401 Unauthorized — токен невалиден (отдельный вопрос — сначала
 *      попробовать refresh; в Supabase SDK это автоматически)
 *    • 403 Forbidden — RLS отказала; данные/права не изменятся от retry
 *    • 404 Not Found — родитель удалён/несуществует
 *    • 409 Conflict — unique violation
 *    • 422 Unprocessable Entity — валидация
 *    • PostgreSQL error codes:
 *        - 23xxx (integrity constraint violation) кроме 23503 deferred
 *        - 23503 FK violation — обычно permanent (родитель не существует)
 *        - 23505 unique_violation
 *        - 42xxx (syntax/access)
 *
 *  unknown — не смогли определить; default retryable но с меньшим
 *    лимитом попыток.
 */

import type { OutboxErrorCategory } from './types';

/**
 * Извлекает строковое представление ошибки из произвольного value.
 */
export function describeError(error: unknown): string {
  if (error == null) return 'unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const rec = error as Record<string, unknown>;
    // Supabase PostgrestError shape: { message, code, details, hint }
    if (typeof rec.message === 'string') return rec.message;
    try {
      return JSON.stringify(error);
    } catch {
      return 'unknown error';
    }
  }
  return String(error);
}

/**
 * Главная функция классификации. Возвращает категорию + понятное
 * сообщение для пользователя.
 */
export function classifyError(error: unknown): {
  category: OutboxErrorCategory;
  message: string;
  /**
   * Если категория retryable — сколько раз можно повторять до перевода
   * в dead_letter. Для unknown ограничиваем сильнее чем для retryable.
   */
  maxAttempts: number;
} {
  const message = describeError(error);

  // 1. Сетевые ошибки — самый частый случай. fetch падает с TypeError.
  if (error instanceof TypeError) {
    return { category: 'retryable', message, maxAttempts: 12 };
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return { category: 'retryable', message, maxAttempts: 12 };
  }
  if (/network|fetch failed|ECONNRESET|ETIMEDOUT|ENOTFOUND/i.test(message)) {
    return { category: 'retryable', message, maxAttempts: 12 };
  }

  // 2. Supabase PostgrestError имеет .code и .status.
  const rec = (typeof error === 'object' && error !== null) ? (error as Record<string, unknown>) : null;
  const status = rec && typeof rec.status === 'number' ? rec.status : null;
  const pgCode = rec && typeof rec.code === 'string' ? rec.code : null;

  // 3. PostgreSQL коды.
  if (pgCode) {
    // 23503 FK violation: parent row missing. Это обычно permanent —
    // нужно либо отправить родителя сначала (это решает dependency graph
    // в 1.18.2), либо признать что родитель удалён → permanent.
    if (pgCode === '23503') {
      return { category: 'permanent', message: `FK violation: ${message}`, maxAttempts: 0 };
    }
    // 23505 unique violation: дубликат. Permanent — две одинаковых
    // записи не могут существовать.
    if (pgCode === '23505') {
      return { category: 'permanent', message: `Конфликт: запись уже существует. ${message}`, maxAttempts: 0 };
    }
    // 23xxx прочие integrity violations.
    if (pgCode.startsWith('23')) {
      return { category: 'permanent', message: `Нарушение целостности: ${message}`, maxAttempts: 0 };
    }
    // 42xxx syntax/access.
    if (pgCode.startsWith('42')) {
      return { category: 'permanent', message: `Ошибка доступа/синтаксиса: ${message}`, maxAttempts: 0 };
    }
    // PGRST116 (PostgREST: no rows) — permanent для update/delete.
    if (pgCode === 'PGRST116') {
      return { category: 'permanent', message: `Запись не найдена: ${message}`, maxAttempts: 0 };
    }
  }

  // 4. HTTP статусы.
  if (typeof status === 'number') {
    if (status >= 500) {
      return { category: 'retryable', message: `Сервер недоступен (${status}): ${message}`, maxAttempts: 12 };
    }
    if (status === 429) {
      return { category: 'retryable', message: `Rate limit (429): ${message}`, maxAttempts: 8 };
    }
    if (status === 401 || status === 403) {
      return { category: 'permanent', message: `Нет доступа (${status}): ${message}`, maxAttempts: 0 };
    }
    if (status === 400 || status === 404 || status === 409 || status === 422) {
      return { category: 'permanent', message: `Ошибка данных (${status}): ${message}`, maxAttempts: 0 };
    }
    if (status >= 400 && status < 500) {
      return { category: 'permanent', message: `Клиентская ошибка (${status}): ${message}`, maxAttempts: 0 };
    }
  }

  // 5. Если ничего не подошло — unknown.
  return { category: 'unknown', message, maxAttempts: 4 };
}
